import { useState, useRef, useEffect } from "react";
import { CheckCircle2, Heart, LoaderCircle, MessageCircle, Play, Share2, WifiOff } from "lucide-react";
import { App as CapacitorApp } from '@capacitor/app';
import { Post, MediaHistoryItem } from "../../types";
import { ClueBadge } from "../shared/components/ClueBadge";
import { FollowButton } from "../shared/components/FollowButton";
import { incrementMediaCount, toggleMediaLike } from "../../backend/mediaService";
import { resharePost, shareContent } from "../../backend/sharingService";
import { ContentActionsMenu } from "../shared/components/ContentActionsMenu";
import { subscribeToBlockedUsers } from "../../backend/moderationService";
import { useMinuteClock } from "../../hooks/useMinuteClock";
import { formatRelativeTime } from "../../utils/relativeTime";
import { useInfiniteFeed } from "../../hooks/useInfiniteFeed";
import { PostCommentsSection } from "./PostCommentsSection";
import { PullToRefresh } from "../shared/components/PullToRefresh";

interface HomeTabProps {
  onTabChange?: (
    tab: "shorts" | "longs" | "profile" | "create" | "live",
  ) => void;
  mediaHistory?: MediaHistoryItem[];
  onOpenSubscribeModal?: (creatorName: string, creatorUid?: string) => void;
  currentUser?: import("../../types").AppUser | null;
  onOpenAuth: () => void;
  highlightedMedia?: MediaHistoryItem;
  onOpenProfile?: (uid: string) => void;
}

const DEFAULT_AVATAR = "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100";

export function HomeTab({
  onTabChange,
  currentUser,
  onOpenAuth,
  onOpenProfile,
}: HomeTabProps) {
  const relativeTimeNow = useMinuteClock();
  const {
    posts,
    loadMore,
    refreshFeed,
    isLoading,
    hasMore,
    error: feedError,
    isOffline,
    updateCommentCount,
    updateLike,
    removePost
  } = useInfiniteFeed({ pageSize: 10, currentUserId: currentUser?.uid });

  const feedScrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const likeInFlightRef = useRef(new Set<string>());
  const [openCommentPostIds, setOpenCommentPostIds] = useState<Set<string>>(new Set());
  const [actionError, setActionError] = useState("");
  const [blockedUserIds, setBlockedUserIds] = useState<Set<string>>(new Set());
  const [blockedUsersReady, setBlockedUsersReady] = useState(!currentUser?.uid);
  const [blockedUsersError, setBlockedUsersError] = useState("");

  // Capacitor App State and Back Button handlers
  useEffect(() => {
    const backgroundSub = CapacitorApp.addListener('appStateChange', ({ isActive }) => {
      if (!isActive) {
        document.querySelectorAll('video').forEach(v => v.pause());
      }
    });

    const backSub = CapacitorApp.addListener('backButton', () => {
      if (window.history.length > 1) {
        window.history.back();
      }
    });

    return () => {
      backgroundSub.then(sub => sub.remove()).catch(() => {});
      backSub.then(sub => sub.remove()).catch(() => {});
    };
  }, []);

  useEffect(() => {
    if (!currentUser?.uid) {
      setBlockedUserIds(new Set());
      setBlockedUsersReady(true);
      return;
    }
    setBlockedUsersReady(false);
    setBlockedUsersError("");
    return subscribeToBlockedUsers(
      currentUser.uid,
      (blocked) => {
        setBlockedUserIds(blocked);
        setBlockedUsersReady(true);
      },
      (error) => {
        setBlockedUsersError(error.message);
        setBlockedUsersReady(true);
      },
    );
  }, [currentUser?.uid]);

  // Infinite scroll IntersectionObserver with container root
  useEffect(() => {
    const root = feedScrollRef.current;
    const sentinel = sentinelRef.current;
    if (!root || !sentinel || !hasMore || feedError) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0]?.isIntersecting && hasMore && !feedError) loadMore();
      },
      { root, rootMargin: "600px" }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [feedError, hasMore, loadMore]);

  const toggleLike = async (post: Post) => {
    if (!currentUser) {
      onOpenAuth();
      return;
    }
    if (likeInFlightRef.current.has(post.id)) return;
    likeInFlightRef.current.add(post.id);
    setActionError("");
    const optimisticLiked = !post.isLiked;
    const optimisticDelta = optimisticLiked ? 1 : -1;
    updateLike(post.id, optimisticLiked, optimisticDelta);

    try {
      const liked = await toggleMediaLike(post.id, currentUser.uid);
      if (liked !== optimisticLiked) {
        updateLike(post.id, liked, (liked ? 1 : 0) - (optimisticLiked ? 1 : 0));
      }
    } catch (error) {
      updateLike(post.id, post.isLiked ?? false, -optimisticDelta);
      setActionError(error instanceof Error ? error.message : "Could not update your like.");
    } finally {
      likeInFlightRef.current.delete(post.id);
    }
  };

  const sharePost = async (post: Post) => {
    try {
      await shareContent("post", post.id, post.content || "Clue200 post");
      await incrementMediaCount(post.id, "shareCount");
    } catch (error) {
      if ((error as { name?: string }).name !== "AbortError") {
        console.error("Post share failed:", error);
        setActionError("Could not share this post. Please try again.");
      }
    }
  };

  const handleReshare = async (post: Post) => {
    if (!currentUser) {
      onOpenAuth();
      return;
    }
    if (!post.ownerUid) return;
    try {
      await resharePost(post.id, post.ownerUid);
    } catch (error) {
      console.error("Post reshare failed:", error);
      setActionError("Could not reshare this post. Please try again.");
    }
  };

  useEffect(() => {
    if (!actionError) return;
    const timeout = window.setTimeout(() => setActionError(""), 3500);
    return () => window.clearTimeout(timeout);
  }, [actionError]);

  return (
    <div
      id="home-feed-container"
      className="flex h-full min-h-0 w-full flex-col overflow-hidden bg-slate-50 items-center"
    >
      <div className="w-full max-w-xl mx-auto flex flex-col h-full min-h-0">
        {/* Offline Banner */}
        {isOffline && (
          <div role="status" className="bg-amber-600 text-white px-3 py-1.5 text-xs font-semibold flex items-center justify-center gap-1.5 shrink-0">
            <WifiOff className="w-3.5 h-3.5" />
            You are offline. Showing cached feed.
          </div>
        )}

        {/* Posts Feed Scroll Container */}
        <div
          ref={feedScrollRef}
          id="posts-feed-scroll"
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain flex flex-col"
        >
          <PullToRefresh onRefresh={refreshFeed} scrollRef={feedScrollRef}>
          {/* Create Area */}
          <div className="bg-white p-4 mb-2 border-b border-slate-200 shrink-0">
            <div className="flex items-center gap-3">
              <img
                src={currentUser?.photoURL || DEFAULT_AVATAR}
                width="40"
                height="40"
                loading="lazy"
                decoding="async"
                className="w-10 h-10 rounded-full object-cover"
                alt="Me"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = DEFAULT_AVATAR;
                }}
              />
              <button
                onClick={() => onTabChange?.("create")}
                className="flex-1 bg-slate-100 hover:bg-slate-200 transition-colors text-left px-5 py-2.5 rounded-full text-slate-500 text-sm font-medium"
              >
                What's on your mind,{" "}
                {currentUser?.displayName?.split(" ")[0] || "User"}?
              </button>
            </div>
          </div>

          {blockedUsersError && (
            <p role="alert" className="p-4 text-center text-sm text-rose-700">
              Could not load your block list. Feed content is hidden until it is available.
            </p>
          )}
          {actionError && (
            <p role="status" className="fixed bottom-5 left-1/2 z-[120] -translate-x-1/2 rounded-lg bg-rose-700 px-4 py-2 text-xs font-semibold text-white shadow-lg">
              {actionError}
            </p>
          )}
          {isLoading && posts.length === 0 && (
            <div className="space-y-3 p-4" role="status" aria-label="Loading posts">
              {[0, 1, 2].map(index => (
                <div key={index} className="h-72 animate-pulse rounded-xl bg-white shadow-sm border border-slate-200" />
              ))}
            </div>
          )}
          {feedError && (
            <div role="alert" className="p-6 text-center text-sm text-rose-700">
              <p>Could not load posts.</p>
              <button type="button" onClick={loadMore} className="mt-2 font-bold underline">Retry</button>
            </div>
          )}
          {!blockedUsersError && !isLoading && !feedError && posts.length === 0 && (
            <p className="py-12 text-center text-sm text-slate-500">No posts yet.</p>
          )}
          <div id="posts-list" className="flex flex-col space-y-2">
          {posts
            .filter(
              (post) =>
                blockedUsersReady &&
                !blockedUsersError &&
                (!post.ownerUid || !blockedUserIds.has(post.ownerUid)),
            )
            .map((post, index) => (
              <article
                key={post.id}
                className="bg-white border-y border-slate-200 pt-3 pb-4 shadow-sm min-w-0 w-full max-w-full"
              >
                <div className="px-4 flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <img
                      src={post.authorAvatar || DEFAULT_AVATAR}
                      width="40"
                      height="40"
                      loading="lazy"
                      decoding="async"
                      className={`w-10 h-10 rounded-full object-cover border border-slate-100 shadow-sm ${onOpenProfile ? "cursor-pointer hover:ring-2 hover:ring-blue-900/10 transition-all" : ""}`}
                      alt={post.authorName || "User avatar"}
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = DEFAULT_AVATAR;
                      }}
                      onClick={() =>
                        post.ownerUid && onOpenProfile?.(post.ownerUid)
                      }
                    />
                    <div
                      className={`${onOpenProfile ? "cursor-pointer group" : ""}`}
                      onClick={() =>
                        post.ownerUid && onOpenProfile?.(post.ownerUid)
                      }
                    >
                      <div className="flex items-center gap-1">
                        <span className="text-sm font-black text-slate-900 group-hover:text-blue-900">
                          {post.authorName}
                        </span>
                        <ClueBadge id={post.clueId} />
                        <CheckCircle2 className="w-4 h-4 text-blue-600 fill-blue-600" />
                      </div>
                      <div className="text-xs text-slate-500 font-bold uppercase tracking-tighter">
                        Original:{" "}
                        {formatRelativeTime(post.createdAt, relativeTimeNow)}
                        {post.resharedByName &&
                          ` · Reshared by ${post.resharedByName} ${post.resharedAt ? `· ${formatRelativeTime(post.resharedAt, relativeTimeNow)}` : ""}`}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {post.ownerUid && <FollowButton
                      targetUid={post.ownerUid}
                      targetName={post.authorName}
                      currentUser={currentUser}
                      onOpenAuth={onOpenAuth}
                      compact
                    />}
                    <ContentActionsMenu
                      contentId={post.id}
                      contentType="photo"
                      contentOwnerUid={post.ownerUid}
                      contentTitle={post.content}
                      currentUser={currentUser}
                      onOpenAuth={onOpenAuth}
                      onReshare={() => void handleReshare(post)}
                      onDeleted={(id) =>
                        removePost(id)
                      }
                    />
                  </div>
                </div>

                <div className="px-4 pb-3">
                  <p className="text-sm text-slate-800 leading-snug break-words">
                    {post.content}
                  </p>
                </div>

                {post.mediaUrl && (
                  <div className="bg-slate-900 w-full overflow-hidden max-h-[550px] flex items-center justify-center">
                    {post.mediaUrl.endsWith(".mp4") ? (
                      <VideoPostPlayer post={post} rootRef={feedScrollRef} />
                    ) : (
                      <img
                        src={post.thumbnailUrl || post.mediaUrl}
                        width="800"
                        height="600"
                        className="w-full h-auto max-h-[550px] object-contain bg-slate-900"
                        alt={post.content || "Post content"}
                        loading={index === 0 || index === 1 ? "eager" : "lazy"}
                        {...(index === 0 ? { fetchpriority: "high" } : {})}
                        decoding="async"
                      />
                    )}
                  </div>
                )}

                <div className="px-4 py-3 flex items-center justify-between border-b border-slate-100 text-xs font-bold text-slate-500">
                  <div className="flex items-center gap-1">
                    <button type="button" onClick={() => void toggleLike(post)} className="hover:underline">
                      {post.likes.toLocaleString()} likes
                    </button>
                    <span aria-hidden="true">·</span>
                    <button
                      type="button"
                      onClick={() => {
                        if (!currentUser) {
                          onOpenAuth();
                          return;
                        }
                        setOpenCommentPostIds(previous => new Set(previous).add(post.id));
                      }}
                      className="hover:underline"
                    >
                      {post.comments.toLocaleString()} comments
                    </button>
                  </div>
                </div>

                <div className="px-2 pt-1 flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => void toggleLike(post)}
                    aria-pressed={post.isLiked}
                    aria-label={post.isLiked ? "Unlike" : "Like"}
                    className={`flex-1 flex min-h-10 items-center justify-center gap-2 rounded-lg py-2 transition-colors font-bold text-xs ${
                      post.isLiked
                        ? "text-rose-600 bg-rose-50"
                        : "text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    <Heart
                      className={`w-4 h-4 ${post.isLiked ? "fill-current" : ""}`}
                    />
                    Like
                  </button>
                  <button
                    type="button"
                    aria-expanded={openCommentPostIds.has(post.id)}
                    onClick={() => {
                      if (!currentUser) {
                        onOpenAuth();
                        return;
                      }
                      setOpenCommentPostIds(previous => {
                        const next = new Set(previous);
                        if (next.has(post.id)) next.delete(post.id);
                        else next.add(post.id);
                        return next;
                      });
                    }}
                    className="flex-1 flex min-h-10 items-center justify-center gap-2 rounded-lg py-2 text-slate-600 hover:bg-slate-50 transition-colors font-bold text-xs"
                  >
                    <MessageCircle className="w-4 h-4" /> Comment
                  </button>
                  <button
                    type="button"
                    onClick={() => void sharePost(post)}
                    className="flex-1 flex min-h-10 items-center justify-center gap-2 rounded-lg py-2 text-slate-600 hover:bg-slate-50 transition-colors font-bold text-xs"
                  >
                    <Share2 className="w-4 h-4" /> Share
                  </button>
                </div>
                {openCommentPostIds.has(post.id) && (
                  <PostCommentsSection
                    postId={post.id}
                    currentUser={currentUser}
                    onOpenAuth={onOpenAuth}
                    onCommentCountChange={delta => updateCommentCount(post.id, delta)}
                    blockedUserIds={blockedUserIds}
                    onOpenProfile={onOpenProfile}
                  />
                )}
              </article>
            ))}
          </div>
          <div ref={sentinelRef} aria-hidden="true" className="h-1 w-full" />
          {isLoading && posts.length > 0 && (
            <div className="flex justify-center py-4" role="status" aria-label="Loading more posts">
              <LoaderCircle className="h-5 w-5 animate-spin text-blue-800" />
            </div>
          )}
          {!hasMore && posts.length > 0 && (
            <p className="py-5 text-center text-xs font-semibold text-slate-500">You're all caught up</p>
          )}
          </PullToRefresh>
        </div>
      </div>

    </div>
  );
}

const VideoPostPlayer = ({
  post,
  rootRef
}: {
  post: Post;
  rootRef: { current: HTMLDivElement | null };
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const itemRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
          // Pause all other videos in the document to ensure only one plays
          document.querySelectorAll('video').forEach(v => {
            if (v !== videoRef.current) v.pause();
          });
          setIsPlaying(true);
          videoRef.current?.play().catch(() => {});
        } else {
          setIsPlaying(false);
          videoRef.current?.pause();
        }
      },
      { root: rootRef.current, threshold: 0.6 }
    );

    if (itemRef.current) observer.observe(itemRef.current);
    return () => {
      observer.disconnect();
    };
  }, [post.id, rootRef]);

  return (
    <div
      ref={itemRef}
      className="relative w-full aspect-video bg-black flex items-center justify-center"
    >
      <video
        ref={videoRef}
        src={post.mediaUrl}
        poster={post.thumbnailUrl}
        playsInline
        loop
        muted
        preload="none"
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        className="w-full h-full object-contain"
      />
      {!isPlaying && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/10">
          <Play className="w-10 h-10 text-white/50 fill-white/20" />
        </div>
      )}
    </div>
  );
};
